import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, combineLatest, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ActuacionContractual,
  CreateActuacionContractualRequest,
  UpdateActuacionContractualRequest
} from '../../models/actuacion-contractual.interface';
import { ACTUACION_CONTRACTUAL_LABELS } from './actuacion-contractual.labels';
import * as ActuacionContractualActions from '../../store/actuacion-contractual.actions';
import * as ActuacionContractualSelectors from '../../store/actuacion-contractual.selectors';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LoggerService } from '../../../../core/services/logger.service';
import { TrackByUtils } from '../../../../shared/utils/trackby.utils';

@Component({
  selector: 'app-actuacion-contractual',
  templateUrl: './actuacion-contractual.component.html',
  styleUrls: ['./actuacion-contractual.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatPaginatorModule,
    RouterModule
  ]
})
export class ActuacionContractualComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly notificationService = inject(NotificationService);
  private readonly logger = inject(LoggerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly labels = ACTUACION_CONTRACTUAL_LABELS;

  // Observables del store
  actuacionesContractuales$ = this.store.select(
    ActuacionContractualSelectors.selectAllActuacionesContractuales
  );
  loading$ = this.store.select(ActuacionContractualSelectors.selectActuacionContractualLoading);
  error$ = this.store.select(ActuacionContractualSelectors.selectActuacionContractualError);
  creating$ = this.store.select(ActuacionContractualSelectors.selectActuacionContractualCreating);
  updating$ = this.store.select(ActuacionContractualSelectors.selectActuacionContractualUpdating);

  // Filtros por columnas
  columnFilters: { tipo: string; status: string } = {
    tipo: '',
    status: ''
  };

  // Subjects de filtrado reactivo
  private readonly searchSubject = new BehaviorSubject<string>('');
  private readonly columnFiltersSubject = new BehaviorSubject<{ tipo: string; status: string }>({
    tipo: '',
    status: ''
  });

  // Data source para la tabla con paginación
  dataSource = new MatTableDataSource<ActuacionContractual>([]);

  // Subject para manejar los datos de la tabla
  private tableDataSubject = new BehaviorSubject<ActuacionContractual[]>([]);
  tableData$ = this.tableDataSubject.asObservable();

  // Observable combinado para mostrar actuaciones aplicando búsqueda y filtros por columna
  displayActuaciones$ = combineLatest([
    this.actuacionesContractuales$,
    this.searchSubject,
    this.columnFiltersSubject
  ]).pipe(
    map(([actuaciones, globalSearch, colFilters]) => {
      let result = actuaciones;

      // Búsqueda general
      const globalQuery = (globalSearch || '').toLowerCase().trim();
      if (globalQuery) {
        result = result.filter(a => a.tipo.toLowerCase().includes(globalQuery));
      }

      // Filtros por columna
      const { tipo, status } = colFilters;
      if (tipo && tipo.trim()) {
        const q = tipo.toLowerCase().trim();
        result = result.filter(a => a.tipo.toLowerCase().includes(q));
      }
      if (status && status.trim()) {
        const q = status.toLowerCase().trim();
        result = result.filter(a => (a.estado ? 'activo' : 'inactivo').includes(q));
      }

      return result;
    })
  );

  // Propiedades para paginación
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Formularios
  actuacionContractualForm!: FormGroup;

  // Estados del componente
  showSearchPanel = false;
  searchQuery = '';
  isEditing = false;
  editingActuacionContractual: ActuacionContractual | null = null;
  detailBeingViewed: ActuacionContractual | null = null;

  // Estados para rastrear operaciones
  wasCreating = false;
  wasUpdating = false;

  // Tabla
  tableColumns: string[] = ['id', 'tipo', 'status', 'actions'];
  trackByActuacionId = TrackByUtils.trackByProperty<ActuacionContractual>('actuacionId');

  // Referencias a templates
  @ViewChild('createActuacionContractualModal')
  createActuacionContractualModal!: TemplateRef<unknown>;
  @ViewChild('detailModal') detailModal!: TemplateRef<unknown>;
  @ViewChild('paginator') set paginator(p: MatPaginator) {
    if (p) {
      this._paginator = p;
      this.dataSource.paginator = p;
    }
  }
  private _paginator!: MatPaginator;

  ngOnInit(): void {
    this.initializeForms();
    this.setupPagination();
    this.loadActuacionesContractuales();
    this.subscribeToError();
    // Inicializar subjects de filtros
    this.searchSubject.next('');
    this.columnFiltersSubject.next({ tipo: '', status: '' });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Configura la paginación
   */
  private setupPagination(): void {
    // Suscribirse a los cambios en displayActuaciones$ para actualizar el data source
    this.displayActuaciones$.pipe(takeUntil(this.destroy$)).subscribe(actuaciones => {
      this.logger.debug('Updating data source with actuaciones', { count: actuaciones.length });

      // Actualizar el data source existente
      this.dataSource.data = actuaciones;

      // Asegurar que el paginador esté conectado
      if (this.paginator && !this.dataSource.paginator) {
        this.dataSource.paginator = this.paginator;
      }

      // Actualizar el subject para mantener la sincronización
      this.tableDataSubject.next(actuaciones);

      this.logger.debug('Data source updated', {
        dataLength: this.dataSource.data.length,
        paginatorConnected: !!this.dataSource.paginator,
        paginatorLength: this.paginator?.length
      });
    });
  }

  /**
   * Se ejecuta después de que se inicializa la vista
   */
  ngAfterViewInit(): void {
    // Conectar el paginador al data source inmediatamente
    this.dataSource.paginator = this._paginator;

    // Asegurar que el paginador esté configurado correctamente
    if (this._paginator) {
      this._paginator.pageSize = this.pageSize;
      this._paginator.pageSizeOptions = this.pageSizeOptions;
    }

    // Log para debug
    this.logger.debug('ngAfterViewInit - Paginator connected', {
      paginatorExists: !!this._paginator,
      dataSourcePaginator: !!this.dataSource.paginator,
      dataLength: this.dataSource.data.length,
      pageSize: this._paginator?.pageSize,
      pageSizeOptions: this._paginator?.pageSizeOptions
    });
  }

  /**
   * Maneja los cambios de página
   */
  onPageChange(): void {
    // El paginador se maneja automáticamente por el MatTableDataSource
    // Este método puede usarse para logging o acciones adicionales
    this.logger.debug('Page changed', {
      pageIndex: this._paginator?.pageIndex,
      pageSize: this._paginator?.pageSize,
      length: this._paginator?.length,
      dataSourceLength: this.dataSource.data.length,
      filteredDataLength: this.dataSource.filteredData.length
    });
  }

  /**
   * Verifica el estado del paginador
   */
  private checkPaginatorState(): void {
    this.logger.debug('Paginator state check', {
      paginatorExists: !!this._paginator,
      dataSourcePaginator: !!this.dataSource.paginator,
      dataSourceDataLength: this.dataSource.data.length,
      paginatorLength: this._paginator?.length,
      paginatorPageSize: this._paginator?.pageSize,
      paginatorPageIndex: this._paginator?.pageIndex
    });

    // Si el paginador no está conectado, intentar reconectarlo
    if (this._paginator && !this.dataSource.paginator) {
      this.logger.debug('Reconnecting paginator to data source');
      this.dataSource.paginator = this._paginator;
    }
  }

  /**
   * Aplica filtros y resetea la paginación
   */
  applyFilters(): void {
    // push current values to subjects
    this.searchSubject.next(this.searchQuery);
    this.columnFiltersSubject.next({ ...this.columnFilters });
    // Reset pagination to first page when filters change
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  /**
   * Limpia los filtros y resetea la paginación
   */
  clearFilters(): void {
    this.searchQuery = '';
    this.columnFilters = { tipo: '', status: '' };
    this.searchSubject.next('');
    this.columnFiltersSubject.next({ tipo: '', status: '' });
    // Reset pagination to first page when filters change
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  private initializeForms(): void {
    this.actuacionContractualForm = this.formBuilder.group({
      tipo: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      estado: [true]
    });
  }

  loadActuacionesContractuales(): void {
    this.store.dispatch(ActuacionContractualActions.loadActuacionesContractuales({ force: false }));
  }

  toggleSearch(): void {
    this.showSearchPanel = !this.showSearchPanel;
    if (!this.showSearchPanel) {
      this.resetFilters();
    }
  }

  searchActuacionesContractuales(query: string): void {
    this.searchQuery = query;
    this.searchSubject.next(this.searchQuery);
    if (this._paginator) this._paginator.firstPage();
  }

  onColumnFilterChange(field: keyof typeof this.columnFilters, value: string): void {
    this.columnFilters[field] = value;
    this.columnFiltersSubject.next({ ...this.columnFilters });
    if (this._paginator) this._paginator.firstPage();
  }

  resetFilters(): void {
    this.clearFilters();
  }

  openCreateModal(actuacion?: ActuacionContractual): void {
    this.isEditing = !!actuacion;
    this.editingActuacionContractual = actuacion || null;

    if (actuacion) {
      this.actuacionContractualForm.patchValue({
        tipo: actuacion.tipo,
        estado: actuacion.estado
      });
    } else {
      this.actuacionContractualForm.reset({
        tipo: '',
        estado: true
      });
    }

    this.dialog.open(this.createActuacionContractualModal, {
      width: '500px',
      disableClose: true
    });
  }

  closeModal(): void {
    this.dialog.closeAll();
    this.resetForm();
  }

  async saveActuacionContractual(): Promise<void> {
    if (this.actuacionContractualForm.valid) {
      const formData = this.actuacionContractualForm.value;

      if (this.isEditing && this.editingActuacionContractual) {
        // Para editar, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea actualizar esta actuación contractual?',
          'Confirmar Actualización'
        );

        if (confirmed) {
          const updateRequest: UpdateActuacionContractualRequest = {
            Tipo: formData.tipo
          };
          this.store.dispatch(
            ActuacionContractualActions.updateActuacionContractual({
              id: this.editingActuacionContractual.actuacionId,
              actuacionContractual: updateRequest
            })
          );

          // Cerrar modal después de actualizar
          this.closeModal();
        }
      } else {
        // Para crear, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea crear esta actuación contractual?',
          'Confirmar Creación'
        );

        if (confirmed) {
          const createRequest: CreateActuacionContractualRequest = {
            Tipo: formData.tipo
          };
          this.store.dispatch(
            ActuacionContractualActions.createActuacionContractual({
              actuacionContractual: createRequest
            })
          );

          // Cerrar modal después de crear
          this.closeModal();
        }
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  viewDetail(actuacion: ActuacionContractual): void {
    this.detailBeingViewed = actuacion;
    this.dialog.open(this.detailModal, {
      width: '500px',
      disableClose: false
    });
  }

  async toggleActuacionContractualStatus(actuacion: ActuacionContractual): Promise<void> {
    const newStatus = !actuacion.estado;
    const action = newStatus ? 'activar' : 'desactivar';

    const confirmed = await this.notificationService.showConfirmation(
      `¿Está seguro de que desea ${action} esta actuación contractual?`,
      'Confirmar cambio de estado'
    );

    if (confirmed) {
      this.store.dispatch(
        ActuacionContractualActions.toggleActuacionContractualStatus({
          id: actuacion.actuacionId,
          isActive: newStatus
        })
      );

      // El store se actualiza con la respuesta del efecto; no recargamos manualmente
    }
  }

  hasError(controlName: string): boolean {
    const control = this.actuacionContractualForm.get(controlName);
    return !!(control?.invalid && control?.touched);
  }

  getErrorMessage(controlName: string): string {
    const control = this.actuacionContractualForm.get(controlName);

    if (control?.hasError('required')) {
      switch (controlName) {
        case 'tipo':
          return 'El tipo es obligatorio';
        default:
          return 'Este campo es obligatorio';
      }
    }

    if (control?.hasError('minlength')) {
      switch (controlName) {
        case 'tipo':
          return 'El tipo debe tener al menos 2 caracteres';
        default:
          return 'Longitud mínima no alcanzada';
      }
    }

    if (control?.hasError('maxlength')) {
      switch (controlName) {
        case 'tipo':
          return 'El tipo no puede tener más de 100 caracteres';
        default:
          return 'Longitud máxima excedida';
      }
    }

    return '';
  }

  /**
   * Marca todos los campos del formulario como touched
   */
  private markFormGroupTouched(): void {
    Object.keys(this.actuacionContractualForm.controls).forEach(key => {
      const control = this.actuacionContractualForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Resetea el formulario
   */
  private resetForm(): void {
    this.actuacionContractualForm.reset({
      tipo: '',
      estado: true
    });
    this.isEditing = false;
    this.editingActuacionContractual = null;
  }

  private subscribeToError(): void {
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        this.errorHandler.handleError(error);
      }
    });

    // Suscripción a creación exitosa
    this.creating$.pipe(takeUntil(this.destroy$)).subscribe(creating => {
      if (!creating && this.wasCreating) {
        this.notificationService.showSuccessModal('Actuación contractual creada exitosamente');
      }
      this.wasCreating = creating;
    });

    // Suscripción a actualización exitosa
    this.updating$.pipe(takeUntil(this.destroy$)).subscribe(updating => {
      if (!updating && this.wasUpdating) {
        this.notificationService.showSuccessModal('Actuación contractual actualizada exitosamente');
      }
      this.wasUpdating = updating;
    });
  }
}
